import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import Button from "../Button";
import FormTextInput from "../FormTextInput";
import {
    MakeMeasuredFormValues,
    defaultMakeMeasuredFormValues,
    makeMeasuredFormValidationSchema,
} from "./MakeMeasuredFormValues";
import { errorToast, successToast } from "../../toast";
import { useMakeMeasured } from "../../api/emissions-sources";
import { Alert } from "@mui/material";
import FormRadioGroup from "../FormRadioGroup";

type MakeMeasuredFormProps = {
    emissionsSourceid: number;
    onCancel: () => void;
    onSubmit: () => void;
};

const MakeMeasuredForm = ({
    emissionsSourceid,
    onCancel,
    onSubmit,
}: MakeMeasuredFormProps) => {
    const methods = useForm<MakeMeasuredFormValues>({
        mode: "onSubmit",
        resolver: yupResolver(makeMeasuredFormValidationSchema),
        defaultValues: defaultMakeMeasuredFormValues,
    });

    const [request, isSubmitting] = useMakeMeasured(emissionsSourceid);

    const handleMakeMeasuredFormSubmission = async (
        value: MakeMeasuredFormValues
    ) => {
        const response = await request({
            ...value,
            isPrimary: isPrimary === 0 ? false : true,
        });

        if (!response.success) {
            errorToast(
                "There was a problem saving the emissions source - please try again"
            );
            return;
        }

        successToast("Emissions source updated");
        onSubmit();
    };

    const isPrimary = methods.watch("isPrimary");

    return (
        <>
            <form
                onSubmit={methods.handleSubmit(
                    handleMakeMeasuredFormSubmission
                )}
            >
                <Alert severity="info" className="items-center">
                    Changing this emissions source to measured will enable
                    linking a data source and recording activity data against
                    it. This action cannot be undone.
                </Alert>
                <div className="space-y-4 mt-5">
                    <Controller
                        name="isPrimary"
                        control={methods.control}
                        render={({ field: { onChange, value } }) => (
                            <FormRadioGroup
                                required
                                label="Select data status"
                                value={value}
                                onChange={onChange}
                                defaultValue={value}
                                error={methods.formState.errors.isPrimary}
                                options={[
                                    {
                                        value: 1,
                                        label: "Measured - Primary",
                                    },
                                    {
                                        value: 0,
                                        label: "Measured - Secondary",
                                    },
                                ]}
                            />
                        )}
                    />
                    <FormTextInput
                        required
                        type="text"
                        label="Justification"
                        placeholder="Enter justification for change"
                        {...methods.register("justification")}
                        error={methods.formState.errors.justification}
                    />
                </div>
                <div className="flex justify-end gap-4 mt-10">
                    <Button
                        variant="transparent"
                        type="button"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button disabled={isSubmitting}>Make Measured</Button>
                </div>
            </form>
        </>
    );
};

export default MakeMeasuredForm;
